import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchUserGoals,
  createGoal as createGoalAction,
  updateGoal as updateGoalAction,
  deleteGoal as deleteGoalAction,
  toggleGoalActive,
  selectGoals,
  selectActiveGoals,
  selectGoalsLoading,
  selectGoalsError,
} from '../store/slices/goalsSlice';
import { Goal } from '../types/achievements';
import goalsService from '../services/goals.service';

/**
 * Custom hook for accessing and manipulating goals data
 * Works with the simpler goals system matching the original app
 */
export const useSimpleGoals = () => {
  const dispatch = useAppDispatch();
  
  // State selectors
  const goals = useAppSelector(selectGoals);
  const activeGoals = useAppSelector(selectActiveGoals);
  const isLoading = useAppSelector(selectGoalsLoading);
  const error = useAppSelector(selectGoalsError);
  const currentUser = useAppSelector((state) => state.auth.user);
  
  // Goal IDs for compatibility
  const goalIds = goals.map(g => g.id).filter(Boolean) as string[];
  
  // Data loading
  const loadUserGoals = useCallback((userId: string) => {
    return dispatch(fetchUserGoals(userId)).unwrap();
  }, [dispatch]);
  
  // Goal management
  const createGoal = useCallback(async (goalData: Partial<Goal>): Promise<string> => {
    if (!currentUser?.uid) throw new Error('User not authenticated');
    const uid = currentUser.uid;
    const newGoal: Goal = {
      id: '',
      type: goalData.type || 'time',
      frequency: goalData.frequency || 'weekly',
      um: goalData.um || 'hours',
      umShort: goalData.umShort || 'h',
      isActive: goalData.isActive ?? true,
      targetValue: goalData.targetValue || 10,
      dateUpdated: Date.now(),
      createdAt: Date.now(),
    };
    
    const result = await dispatch(createGoalAction({ uid, goal: newGoal })).unwrap();
    return result.id || '';
  }, [dispatch, currentUser]);
  
  const updateGoal = useCallback(async (goalId: string, updates: Partial<Goal>) => {
    if (!currentUser?.uid) throw new Error('User not authenticated');
    const uid = currentUser.uid;
    const existingGoal = goals.find(g => g.id === goalId);
    if (!existingGoal) throw new Error('Goal not found');
    
    const updatedGoal: Goal = {
      ...existingGoal,
      ...updates,
      dateUpdated: Date.now(),
    };
    
    await dispatch(updateGoalAction({ uid, goal: updatedGoal })).unwrap();
  }, [dispatch, goals, currentUser]);
  
  const deleteGoal = useCallback(async (goalId: string) => {
    if (!currentUser?.uid) throw new Error('User not authenticated');
    const uid = currentUser.uid;
    await dispatch(deleteGoalAction({ uid, goalId })).unwrap();
  }, [dispatch, currentUser]);
  
  const toggleGoal = useCallback(async (goalId: string) => {
    if (!currentUser?.uid) throw new Error('User not authenticated');
    const uid = currentUser.uid;
    await dispatch(toggleGoalActive({ uid, goalId })).unwrap();
  }, [dispatch, currentUser]);
  
  // Get current progress
  const getGoalProgress = useCallback(async (uid: string) => {
    return await goalsService.getCurrentGoalProgress(uid);
  }, []);
  
  // Update progress from training
  const updateProgressFromTraining = useCallback(async (
    uid: string, 
    trainingDuration: number, 
    trainingDate: number
  ) => {
    await goalsService.updateGoalProgressFromTraining(uid, trainingDuration, trainingDate);
  }, []);
  
  // Additional compatibility methods
  const getGoal = useCallback(async (goalId: string) => {
    const goal = goals.find(g => g.id === goalId);
    if (goal) return goal;
    
    // If not in state, fetch from service
    if (!currentUser?.uid) throw new Error('User not authenticated');
    const uid = currentUser.uid;
    const userGoal = await goalsService.getUserGoal(uid, goalId);
    return userGoal;
  }, [goals, currentUser]);
  
  const updateGoalProgress = useCallback(async (goalId: string, progressData: any) => {
    if (!currentUser?.uid) throw new Error('User not authenticated');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const uid = currentUser.uid;
    const goal = goals.find(g => g.id === goalId);
    if (!goal) throw new Error('Goal not found');
    
    // For simple goals, we just update the goal itself
    await updateGoal(goalId, progressData);
  }, [goals, updateGoal, currentUser]);
  
  return {
    // State
    goals,
    goalIds,
    activeGoals,
    isLoading,
    error,
    
    // Actions
    loadUserGoals,
    createGoal,
    updateGoal,
    deleteGoal,
    toggleGoal,
    getGoal,
    getGoalProgress,
    updateProgressFromTraining,
    updateGoalProgress,
    
    // For compatibility with existing components
    templates: {},
    achievements: {},
    templatesByType: {},
    achievementsByCategory: {},
    completedGoals: goals.filter(g => !g.isActive),
    goalStats: {
      totalGoals: goals.length,
      activeGoals: activeGoals.length,
      completedGoals: goals.filter(g => !g.isActive).length,
      abandonedGoals: 0,
      failedGoals: 0,
      averageCompletionRate: 0,
      byType: {},
      byFrequency: {},
    },
  };
};